import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import type { PatCreateResponseDTO, PatRowDTO, TokenCreateInput } from "@colab/shared";

// Personal Access Tokens for MCP / headless API access. The same endpoints
// exist under two mounts with identical shapes: /portal/tokens (partner) and
// /settings/tokens (internal). The scope selects which to call; tokens are
// always tied to the authenticated user, so each scope lists only the caller's
// own tokens.
export type TokenScope = "portal" | "settings";

const basePath = (scope: TokenScope) => `/${scope}/tokens`;
const tokensKey = (scope: TokenScope) => ["api-tokens", scope] as const;

// `enabled` lets callers defer the fetch until the user opts in (the panel
// keeps the token list collapsed until clicked). This avoids firing the
// request on initial render of the settings page.
export function useApiTokens(scope: TokenScope, enabled = true) {
  return useQuery<PatRowDTO[], ApiClientError>({
    queryKey: tokensKey(scope),
    queryFn: () => apiFetch<PatRowDTO[]>(basePath(scope)),
    enabled,
  });
}

export function useCreateApiToken(scope: TokenScope) {
  const queryClient = useQueryClient();
  return useMutation<PatCreateResponseDTO, ApiClientError, TokenCreateInput>({
    mutationFn: (input) =>
      apiFetch<PatCreateResponseDTO>(basePath(scope), {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tokensKey(scope) });
    },
    onError: (err) => toast.error(err.message ?? "Failed to create token"),
  });
}

export function useRevokeApiToken(scope: TokenScope) {
  const queryClient = useQueryClient();
  return useMutation<{ ok: true }, ApiClientError, string>({
    mutationFn: (id) =>
      apiFetch<{ ok: true }>(`${basePath(scope)}/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tokensKey(scope) });
      toast.success("Token revoked");
    },
    onError: (err) => toast.error(err.message ?? "Failed to revoke token"),
  });
}
